// Small pure formatters for the ציר זמן rows. Kept local to the timeline folder
// on purpose (no cross-tab import) — the calls tab has its own clock formatter
// with a different job: it renders a player position, this one renders a quiet,
// ROUNDED human label with the exact value moved to a tooltip.

// ── Call duration ────────────────────────────────────────────────────────────

// The server currently ships the duration inside the free-text description:
//   `Call duration: ${call.duration || 'Unknown'}`   (contacts.service.ts)
// `call.duration` is a STRING OF SECONDS on the call doc (the calls tab parses
// it the same way). This reader is deliberately tolerant: it also accepts the
// Hebrew label and a clock-shaped value, so if the server text is ever
// translated or reshaped the row keeps working instead of printing raw English.
const CALL_DURATION_RE = /^\s*(?:call\s*duration|משך\s*(?:ה?שיחה))\s*:\s*(.*?)\s*$/i;
const CLOCK_RE = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/;

export type ParsedCallDuration = {
  /** true when the description WAS the duration line — the row must not also print it. */
  matched: boolean;
  /** null for 'Unknown' / unparseable / zero-length calls — render no label at all. */
  seconds: number | null;
};

export function parseCallDurationFromDescription(description: string): ParsedCallDuration {
  const match = CALL_DURATION_RE.exec(description ?? "");
  if (!match) return { matched: false, seconds: null };

  const raw = (match[1] ?? "").trim();
  return { matched: true, seconds: parseDurationValue(raw) };
}

function parseDurationValue(raw: string): number | null {
  if (!raw || /^unknown$/i.test(raw) || raw === "****") return null;

  if (/^\d+$/.test(raw)) {
    const seconds = Number(raw);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }

  const clock = CLOCK_RE.exec(raw);
  if (clock) {
    const seconds =
      Number(clock[1] ?? 0) * 3600 + Number(clock[2]) * 60 + Number(clock[3]);
    return seconds > 0 ? seconds : null;
  }

  return null;
}

// Hebrew counts read badly as bare numerals at 1 and 2 ("1 דקות", "2 דקות"),
// so those two get their spelled forms and everything else stays numeric.
const secondsWord = (n: number): string =>
  n === 1 ? "שנייה אחת" : n === 2 ? "שתי שניות" : `${n} שניות`;

const minutesWord = (n: number): string =>
  n === 1 ? "דקה אחת" : n === 2 ? "שתי דקות" : `${n} דקות`;

/** `standalone` keeps "שעה אחת" alone but drops to "שעה" before a "ו…" clause. */
const hoursWord = (n: number, standalone: boolean): string =>
  n === 1 ? (standalone ? "שעה אחת" : "שעה") : n === 2 ? "שעתיים" : `${n} שעות`;

/** "ו-12 דקות" before a numeral, "ודקה אחת" before a word. */
const joinWithVav = (text: string): string => (/^\d/.test(text) ? `ו-${text}` : `ו${text}`);

/**
 * Compact, rounded, human duration:
 *   < 1 minute  → exact seconds        ("22 שניות")
 *   < 1 hour    → minutes, rounded     ("3 דקות")
 *   >= 1 hour   → hours + minutes      ("שעה ו-12 דקות", "שעתיים")
 * Returns "" for unknown/zero — the caller renders nothing rather than "0".
 */
export function formatDurationRounded(totalSeconds: number | null | undefined): string {
  if (typeof totalSeconds !== "number" || !Number.isFinite(totalSeconds)) return "";
  const seconds = Math.floor(totalSeconds);
  if (seconds <= 0) return "";

  if (seconds < 60) return secondsWord(seconds);

  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return minutesWord(totalMinutes);

  // Rounding can carry (3599s → 60 minutes), which is why the promotion to
  // hours happens on the ROUNDED minutes and never on the raw seconds.
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) return hoursWord(hours, true);
  return `${hoursWord(hours, false)} ${joinWithVav(minutesWord(minutes))}`;
}

/** Exact "m:ss" (or "h:mm:ss") for the tooltip behind the rounded label. */
export function formatDurationExact(totalSeconds: number | null | undefined): string {
  if (typeof totalSeconds !== "number" || !Number.isFinite(totalSeconds)) return "";
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const paddedSeconds = String(seconds).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`;
}

// ── Actor ────────────────────────────────────────────────────────────────────

/**
 * Short display name for the row's actor chip. The server already resolves the
 * actor inline on every timeline action (`actionUser` = MiniUser with name +
 * photoURL), so attribution costs ZERO extra reads — never look a user up here.
 *
 * Falls back name → id, drops the server's 'Unknown User' placeholder, and
 * trims an email down to its local part: the meta line must stay short, and the
 * full value is on the actor's tooltip anyway.
 */
export function actorDisplayName(user: {
  userId: string;
  userName: string;
}): string {
  const name = (user?.userName ?? "").trim();
  const id = (user?.userId ?? "").trim();
  const candidate = name && name.toLowerCase() !== "unknown user" ? name : id;
  if (!candidate) return "";
  const at = candidate.indexOf("@");
  return at > 0 ? candidate.slice(0, at) : candidate;
}

/** Full "name · email" for the actor tooltip; "" when nothing is known. */
export function actorFullLabel(user: { userId: string; userName: string }): string {
  const rawName = (user?.userName ?? "").trim();
  // Never surface the server's English 'Unknown User' placeholder to a customer.
  const name = rawName.toLowerCase() === "unknown user" ? "" : rawName;
  const id = (user?.userId ?? "").trim();
  if (name && id && name.toLowerCase() !== id.toLowerCase()) return `${name} · ${id}`;
  return name || id;
}
